import { useState, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { Experiment } from "@/types/experiment";
import { getExperimentColumns } from "./experiment-columns";
import { type ExperimentAction, getTargetStatus } from "./experiment-actions";
import { ExperimentDetailDialog } from "./experiment-detail-dialog";
import { EditExperimentDialog } from "./edit-experiment-dialog";
import { RolloutExperimentDialog } from "./rollout-experiment-dialog";
import {
  useUpdateExperimentStatus,
  useDeleteExperiment,
} from "@/hooks/use-experiments";

type DialogState =
  | { type: "idle" }
  | { type: "detail"; experiment: Experiment }
  | { type: "edit"; experiment: Experiment }
  | { type: "rollout"; experiment: Experiment }
  | {
      type: "confirm";
      experiment: Experiment;
      confirmType: "delete" | "complete" | "start";
    };

interface ExperimentTableProps {
  data: Experiment[];
}

export function ExperimentTable({ data }: ExperimentTableProps) {
  const updateStatusMutation = useUpdateExperimentStatus();
  const deleteMutation = useDeleteExperiment();

  const [dialog, setDialog] = useState<DialogState>({ type: "idle" });

  const handleAction = useCallback(
    (experiment: Experiment, action: ExperimentAction) => {
      if (action === "detail") {
        setDialog({ type: "detail", experiment });
        return;
      }
      if (action === "edit") {
        setDialog({ type: "edit", experiment });
        return;
      }
      if (action === "rollout") {
        setDialog({ type: "rollout", experiment });
        return;
      }
      if (action === "delete") {
        setDialog({ type: "confirm", experiment, confirmType: "delete" });
        return;
      }
      if (action === "start") {
        setDialog({ type: "confirm", experiment, confirmType: "start" });
        return;
      }
      if (action === "complete") {
        setDialog({ type: "confirm", experiment, confirmType: "complete" });
        return;
      }

      updateStatusMutation.mutate({
        key: experiment.key,
        status: getTargetStatus(action),
      });
    },
    [updateStatusMutation],
  );

  const handleConfirm = useCallback(() => {
    if (dialog.type !== "confirm") return;

    const onSuccess = () => setDialog({ type: "idle" });

    if (dialog.confirmType === "delete") {
      deleteMutation.mutate(dialog.experiment.key, { onSuccess });
    } else {
      updateStatusMutation.mutate(
        {
          key: dialog.experiment.key,
          status: getTargetStatus(dialog.confirmType),
        },
        { onSuccess },
      );
    }
  }, [dialog, updateStatusMutation, deleteMutation]);

  const columns = useMemo(
    () => getExperimentColumns(handleAction),
    [handleAction],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p>등록된 실험이 없습니다</p>
      </div>
    );
  }

  const confirmConfig = dialog.type === "confirm"
    ? dialog.confirmType === "delete"
      ? {
          title: "실험 삭제",
          description: `'${dialog.experiment.key}' 실험을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
          action: "삭제",
          variant: "destructive" as const,
        }
      : dialog.confirmType === "start"
        ? {
            title: "실험 시작",
            description: `'${dialog.experiment.key}' 실험을 시작하시겠습니까? 시작 후 사용자에게 실험이 배정됩니다.`,
            action: "시작",
            variant: "default" as const,
          }
        : {
            title: "실험 완료",
            description: `'${dialog.experiment.key}' 실험을 완료 처리하시겠습니까? 완료된 실험은 되돌릴 수 없습니다.`,
            action: "완료 처리",
            variant: "default" as const,
          }
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-md border overflow-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ExperimentDetailDialog
        experiment={dialog.type === "detail" ? dialog.experiment : null}
        open={dialog.type === "detail"}
        onOpenChange={(open) => !open && setDialog({ type: "idle" })}
      />

      <EditExperimentDialog
        key={dialog.type === "edit" ? dialog.experiment.key : undefined}
        experiment={dialog.type === "edit" ? dialog.experiment : null}
        open={dialog.type === "edit"}
        onOpenChange={(open) => !open && setDialog({ type: "idle" })}
      />

      <RolloutExperimentDialog
        key={dialog.type === "rollout" ? dialog.experiment.key : undefined}
        experiment={dialog.type === "rollout" ? dialog.experiment : null}
        open={dialog.type === "rollout"}
        onOpenChange={(open) => !open && setDialog({ type: "idle" })}
      />

      {confirmConfig && (
        <Dialog
          open={dialog.type === "confirm"}
          onOpenChange={(open) => !open && setDialog({ type: "idle" })}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{confirmConfig.title}</DialogTitle>
              <DialogDescription>{confirmConfig.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialog({ type: "idle" })}
                disabled={deleteMutation.isPending || updateStatusMutation.isPending}
              >
                취소
              </Button>
              <Button
                variant={confirmConfig.variant}
                onClick={handleConfirm}
                disabled={deleteMutation.isPending || updateStatusMutation.isPending}
              >
                {confirmConfig.action}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
